import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  UserButton,
  OrganizationSwitcher,
  useUser,
  useOrganization,
  useClerk,
} from '@clerk/clerk-react';
import { useApiAuth } from '../lib/useApi';
import {
  LayoutDashboard,
  Package,
  Plug2,
  Wrench,
  Palette,
  CreditCard,
  Search,
  Settings,
  ShoppingBag,
  MessageSquare,
  Bot,
  LogOut,
  Sun,
  Moon,
  Inbox,
  Users,
  Contact,
  MailPlus,
  BarChart2,
  Megaphone,
  ChevronDown,
  ChevronRight,
  SlidersHorizontal,
  Star,
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useOrgFeatures } from '../hooks/useOrgFeatures';

// Uso diario: lo que un agente/admin revisa constantemente (inbox, métricas).
const DAILY_NAV_ITEMS = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, adminOnly: false },
  { path: '/chat', label: 'Chat', icon: MessageSquare, badge: true, adminOnly: false, feature: 'chat_enabled' },
  { path: '/analytics', label: 'Métricas', icon: BarChart2, adminOnly: false, feature: 'chat_enabled' },
  { path: '/order-messages', label: 'Mensajes Pedido', icon: Inbox, adminOnly: false, orderMsgBadge: true },
  { path: '/support-email', label: 'Email Soporte', icon: MailPlus, adminOnly: false },
  { path: '/customers', label: 'Clientes', icon: Contact, adminOnly: false },
  { path: '/reviews', label: 'Opiniones', icon: Star, reviewsBadge: true, adminOnly: false, feature: 'reviews_enabled' },
  { path: '/searches', label: 'Búsquedas', icon: BarChart2, adminOnly: false, feature: 'search_enabled' },
];

// Configuración: paneles que se tocan de forma ocasional (setup, ajustes, integraciones).
const CONFIG_NAV_ITEMS = [
  { path: '/feed', label: 'Feed XML', icon: Package, adminOnly: false },
  { path: '/connectors', label: 'Conectores', icon: Plug2, adminOnly: false },
  { path: '/install', label: 'Instalación', icon: Wrench, adminOnly: false },
  { path: '/customize', label: 'Personalizar', icon: Palette, adminOnly: false },
  { path: '/search-engine', label: 'Motor de búsqueda', icon: Search, adminOnly: false, feature: 'search_enabled' },
  { path: '/merchandising', label: 'Merchandising', icon: ShoppingBag, adminOnly: false, feature: 'search_enabled' },
  { path: '/popups', label: 'Popups', icon: Megaphone, adminOnly: false },
  { path: '/chat-ai', label: 'Chat AI', icon: Bot, adminOnly: false, feature: 'chat_enabled' },
  { path: '/team', label: 'Equipo', icon: Users, adminOnly: false },
  { path: '/billing', label: 'Facturación', icon: CreditCard, adminOnly: false },
];

// Rutas accesibles por org:member (agente): inbox de chat, mensajes de pedido,
// clientes (visión 360 multicanal) y email de soporte — no paneles de configuración/admin.
const AGENT_ALLOWED_PATHS = new Set(['/chat', '/order-messages', '/customers', '/support-email']);

const CONFIG_MENU_STORAGE_KEY = 'sera_config_menu_open';

export default function Layout({ children }) {
  const location = useLocation();
  const { user } = useUser();
  const { membership, organization } = useOrganization();
  const { signOut } = useClerk();
  const { theme, toggle: toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pendingChats, setPendingChats] = useState(0);
  const [pendingOrderMsgs, setPendingOrderMsgs] = useState(0);
  const [pendingReviews, setPendingReviews] = useState(0);
  const [configOpen, setConfigOpen] = useState(() => {
    try {
      return localStorage.getItem(CONFIG_MENU_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Wire up Clerk token to axios
  useApiAuth();

  const { hasFeature, loading: featuresLoading, isAdmin } = useOrgFeatures();

  // org:admin = acceso completo; org:member = solo chat
  const isOrgAdmin = !membership || membership.role === 'org:admin';

  const featureFilter = (item) => {
    // Feature gating: if item has `feature` and the org doesn't have it, hide.
    // While features are still loading we keep the item visible to avoid flicker;
    // super_admins always see everything (hasFeature returns true for them).
    if (!item.feature) return true;
    if (featuresLoading) return true;
    return hasFeature(item.feature);
  };

  const dailyNavItems = (isOrgAdmin
    ? DAILY_NAV_ITEMS
    : DAILY_NAV_ITEMS.filter(item => AGENT_ALLOWED_PATHS.has(item.path))
  ).filter(featureFilter);

  // Los agentes (org:member) no tienen acceso a ningún panel de configuración.
  const configNavItems = isOrgAdmin ? CONFIG_NAV_ITEMS.filter(featureFilter) : [];

  const isConfigRouteActive = configNavItems.some(item => location.pathname === item.path);

  const toggleConfigMenu = () => {
    setConfigOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(CONFIG_MENU_STORAGE_KEY, next ? '1' : '0'); } catch {}
      return next;
    });
  };

  // Auto-expandir el grupo "Configuración" al aterrizar directamente en una de sus rutas
  // (ej: link directo a /billing), para que el usuario no la pierda de vista.
  useEffect(() => {
    if (isConfigRouteActive) setConfigOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // WebSocket for chat badge — listens to worker stream
  useEffect(() => {
    const orgId = organization?.id;
    if (!orgId) return;
    const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = API_BASE.replace(/^https?/, 'wss').replace(/^http/, 'ws');
    let ws;
    let retry;
    const connect = () => {
      try {
        ws = new WebSocket(`${wsBase}/api/chat/ws/workers/${orgId}`);
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.type === 'new_conversation' || msg.type === 'escalated') {
              setPendingChats(n => n + 1);
            } else if (msg.type === 'conversation_assigned' || msg.type === 'conversation_closed') {
              setPendingChats(n => Math.max(0, n - 1));
            }
          } catch {}
        };
        ws.onclose = () => { clearInterval(keepalive); retry = setTimeout(connect, 5000); };
        ws.onerror = () => ws.close();
        const keepalive = setInterval(() => { if (ws.readyState === 1) ws.send('ping'); }, 25000);
      } catch {}
    };
    connect();
    return () => { clearTimeout(retry); try { ws?.close(); } catch {} };
  }, [organization?.id]);

  // Fetch pending order messages count (polling every 60s)
  useEffect(() => {
    const orgId = organization?.id;
    if (!orgId) return;
    const fetchPending = async () => {
      try {
        const { api } = await import('../lib/api');
        const res = await api.get('/api/order-messages/pending-count');
        setPendingOrderMsgs(res.data?.count || 0);
      } catch {}
    };
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, [organization?.id]);

  // Fetch pending reviews count (polling every 60s) — only if reviews feature is enabled
  useEffect(() => {
    const orgId = organization?.id;
    if (!orgId) return;
    if (featuresLoading) return;
    if (!hasFeature('reviews_enabled')) return;
    const fetchReviews = async () => {
      try {
        const { api } = await import('../lib/api');
        const res = await api.get(`/api/reviews?organization_id=${orgId}&status=pending&per_page=1`);
        setPendingReviews(res.data?.total || 0);
      } catch {}
    };
    fetchReviews();
    const interval = setInterval(fetchReviews, 60000);
    return () => clearInterval(interval);
  }, [organization?.id, hasFeature, featuresLoading]);

  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-16'
        } bg-gray-900 border-r border-gray-800 flex flex-col transition-all duration-300 fixed h-full z-20`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-gray-800">
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-bold">S</span>
          </div>
          {sidebarOpen && (
            <span className="text-white font-bold text-lg">Sera</span>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="ml-auto text-gray-500 hover:text-gray-300"
          >
            {sidebarOpen ? '←' : '→'}
          </button>
        </div>

        {/* Org switcher */}
        {sidebarOpen && (
          <div className="px-3 py-3 border-b border-gray-800">
            <OrganizationSwitcher
              hidePersonal
              afterSelectOrganizationUrl="/"
              createOrganizationUrl="/"
              appearance={{
                elements: {
                  rootBox: 'w-full',
                  organizationSwitcherTrigger:
                    'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm hover:bg-gray-700',
                  organizationSwitcherPopoverCard: 'bg-gray-800 border border-gray-700',
                  organizationSwitcherPopoverActionButton: 'text-gray-300 hover:bg-gray-700',
                  organizationPreviewTextContainer: 'text-white',
                }
              }}
            />
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {dailyNavItems.map((item) => {
            const active = location.pathname === item.path;
            const Icon = item.icon;
            const showBadge = item.badge && pendingChats > 0;
            const showOrderBadge = item.orderMsgBadge && pendingOrderMsgs > 0;
            const showReviewsBadge = item.reviewsBadge && pendingReviews > 0;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => {
                  if (item.badge) setPendingChats(0);
                }}
                data-testid={`nav-link-${item.path === '/' ? 'dashboard' : item.path.slice(1)}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <div className="relative flex-shrink-0">
                  <Icon size={16} />
                  {showBadge && (
                    <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                      {pendingChats > 9 ? '9+' : pendingChats}
                    </span>
                  )}
                  {showOrderBadge && (
                    <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                      {pendingOrderMsgs > 9 ? '9+' : pendingOrderMsgs}
                    </span>
                  )}
                  {showReviewsBadge && !showOrderBadge && (
                    <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                      {pendingReviews > 9 ? '9+' : pendingReviews}
                    </span>
                  )}
                </div>
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}

          {configNavItems.length > 0 && (
            <div className="pt-2 mt-1 border-t border-gray-800/70">
              <button
                onClick={toggleConfigMenu}
                data-testid="config-menu-toggle"
                title="Configuración"
                className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isConfigRouteActive
                    ? 'text-indigo-400'
                    : 'text-gray-500 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <SlidersHorizontal size={16} className="flex-shrink-0" />
                {sidebarOpen && (
                  <>
                    <span className="flex-1 text-left">Configuración</span>
                    {configOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </>
                )}
              </button>

              {configOpen && (
                <div data-testid="config-menu-items" className="mt-1 space-y-1">
                  {configNavItems.map((item) => {
                    const active = location.pathname === item.path;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        data-testid={`nav-link-${item.path.slice(1)}`}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                          active
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                        } ${sidebarOpen ? 'pl-6' : ''}`}
                      >
                        <Icon size={15} className="flex-shrink-0" />
                        {sidebarOpen && <span>{item.label}</span>}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {isAdmin && (
            <Link
              to="/admin"
              data-testid="nav-link-admin"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                location.pathname === '/admin'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <Settings size={16} className="flex-shrink-0" />
              {sidebarOpen && <span>Admin Panel</span>}
            </Link>
          )}
        </nav>

        {/* User */}
        <div className="px-3 py-3 border-t border-gray-800">
          <div className="flex items-center gap-3 mb-2">
            <UserButton
              afterSignOutUrl="/sign-in"
              appearance={{ elements: { avatarBox: 'w-8 h-8' } }}
            />
            {sidebarOpen && (
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-medium truncate">
                  {user?.firstName || user?.emailAddresses?.[0]?.emailAddress || ''}
                </p>
                <p className={`text-xs font-medium ${isOrgAdmin ? 'text-indigo-400' : 'text-green-400'}`}>
                  {isOrgAdmin ? 'Admin' : 'Agente'}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={() => signOut({ redirectUrl: '/sign-in' })}
            data-testid="sign-out-btn"
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs text-gray-400 hover:bg-gray-800 hover:text-red-400 transition-colors ${!sidebarOpen ? 'justify-center' : ''}`}
            title="Cerrar sesión"
          >
            <LogOut size={14} className="flex-shrink-0" />
            {sidebarOpen && <span>Cerrar sesión</span>}
          </button>
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            className={`flex items-center gap-2 w-full px-3 py-2 mt-1 rounded-lg text-xs text-gray-400 hover:bg-gray-800 hover:text-white transition-colors ${!sidebarOpen ? 'justify-center' : ''}`}
            title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          >
            {theme === 'dark' ? (
              <Sun size={14} className="flex-shrink-0" />
            ) : (
              <Moon size={14} className="flex-shrink-0" />
            )}
            {sidebarOpen && (
              <span>{theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}</span>
            )}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`flex-1 min-w-0 overflow-hidden ${sidebarOpen ? 'ml-64' : 'ml-16'} transition-all duration-300`}>
        {children}
      </main>
    </div>
  );
}
